import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BuildingStorefrontIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/ApiService';
import { useAuth } from '../../contexts/AuthContext';
import type { Shop, ShopRequest } from '../../types/buildExpansion';
import PageShell from '../ui/PageShell';
import { PageHeader } from '../ui/PageHeader';
import FeatureTip from '../onboarding/FeatureTip';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import { ShopStatusBadge } from './ShopStatusBadge';
import { ShopRequestModal } from './ShopRequestModal';

const MAX_SERVICE_CHIPS = 3;

const ShopCard: React.FC<{ shop: Shop }> = ({ shop }) => {
  const location = [shop.city, shop.state].filter(Boolean).join(', ');
  const visibleServices = shop.services.slice(0, MAX_SERVICE_CHIPS);
  const extraServices = shop.services.length - visibleServices.length;
  const trust = shop.trust_label ?? (shop.is_verified ? 'verified' : shop.is_community_verified ? 'community_verified' : 'listed');

  return (
    <Link
      to={`/shops/${shop.slug}`}
      className="card-interactive block p-4 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50 rounded-xl h-full"
    >
      <div className="flex items-start gap-3">
        {shop.logo_path ? (
          <img
            src={apiService.getFileUrl(shop.logo_path) || shop.logo_path}
            alt=""
            className="w-12 h-12 rounded-lg object-cover border border-white/[0.08] bg-[#0a0a0a] shrink-0"
          />
        ) : (
          <div
            className="w-12 h-12 rounded-lg bg-gradient-brand-tile flex items-center justify-center text-sm font-bold text-white shrink-0"
            aria-hidden
          >
            {(shop.name || '?').charAt(0).toUpperCase()}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h2 className="heading-card flex items-center gap-1.5 flex-wrap">
            <span className="truncate">{shop.name}</span>
            {(trust === 'verified' || trust === 'community_verified') && (
              <ShopStatusBadge trustLabel={trust} compact />
            )}
          </h2>
          {location ? <p className="text-sm text-gray-400 mt-0.5">{location}</p> : null}
        </div>
      </div>
      {visibleServices.length > 0 && (
        <ul className="flex flex-wrap gap-1.5 mt-3" aria-label="Services">
          {visibleServices.map((service) => (
            <li key={service} className="badge bg-[#111] text-gray-300 border border-white/[0.08]">
              {service}
            </li>
          ))}
          {extraServices > 0 && (
            <li className="badge bg-[#111]/60 text-gray-500 border border-white/[0.06]">+{extraServices} more</li>
          )}
        </ul>
      )}
    </Link>
  );
};

const requestStatusLabel = (status: ShopRequest['status']): string => {
  switch (status) {
    case 'pending':
      return 'Pending review';
    case 'needs_info':
      return 'More info requested';
    case 'approved':
      return 'Approved';
    case 'rejected':
      return 'Rejected';
    case 'merged':
      return 'Merged with existing';
    default:
      return status;
  }
};

/** Public shop directory — approved shops only; request flow for new entries. */
export const ShopDirectoryPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [shops, setShops] = useState<Shop[]>([]);
  const [myRequests, setMyRequests] = useState<ShopRequest[]>([]);
  const [total, setTotal] = useState<number | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [activeQuery, setActiveQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requestOpen, setRequestOpen] = useState(false);

  const load = useCallback(async (q: string, targetPage: number) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.getShopDirectory({ q: q || undefined, page: targetPage });
      setShops(result.shops);
      setTotal(result.total);
      if (targetPage === 1 && result.shops.length > 0) setPageSize(result.shops.length);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not load shops.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(activeQuery, page);
  }, [load, activeQuery, page]);

  useEffect(() => {
    if (!isAuthenticated) {
      setMyRequests([]);
      return;
    }
    void apiService.getMyShopRequests().then(setMyRequests).catch(() => setMyRequests([]));
  }, [isAuthenticated]);

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setPageSize(null);
    setActiveQuery(search.trim());
  };

  const hasPrev = page > 1;
  const hasNext = total !== undefined && pageSize !== null && page * pageSize < total;
  const showPagination = hasPrev || hasNext;
  const pendingRequests = myRequests.filter((r) => r.status === 'pending' || r.status === 'needs_info');
  const resolvedRequests = myRequests.filter((r) => r.status !== 'pending' && r.status !== 'needs_info');

  const requestStatusBadgeClass = (status: ShopRequest['status']): string => {
    switch (status) {
      case 'pending':
      case 'needs_info':
        return 'bg-amber-950/50 text-amber-300 border-amber-800';
      case 'approved':
        return 'bg-green-950/50 text-green-300 border-green-800';
      case 'rejected':
        return 'bg-red-950/50 text-red-300 border-red-800';
      default:
        return 'bg-[#111] text-gray-300 border-white/[0.08]';
    }
  };

  return (
    <PageShell width="md">
      <PageHeader
        eyebrow="Directory"
        title="Shops"
        description="Tuning, fabrication, and detail shops behind documented builds. New shops are reviewed before listing."
        actions={
          isAuthenticated ? (
            <Button type="button" variant="primary" onClick={() => setRequestOpen(true)}>
              Request a shop
            </Button>
          ) : null
        }
      />

      <FeatureTip tipId="shops_directory" className="mb-4">
        Credit the shops and contributors behind the work. Request a listing or browse verified portfolios.
      </FeatureTip>

      {isAuthenticated && myRequests.length > 0 && (
        <section aria-labelledby="my-requests-heading" className="mb-6 rounded-xl border border-white/[0.06] bg-[#0c0c0c]/50 p-4">
          <h2 id="my-requests-heading" className="heading-section text-base mb-2">
            Your shop requests
          </h2>
          {pendingRequests.length > 0 ? (
            <>
              <p className="text-xs text-gray-500 mb-2">Pending review</p>
              <ul className="space-y-2 list-none mb-4">
                {pendingRequests.map((req) => (
                  <li key={req.id} className="text-sm text-gray-300 flex flex-wrap items-center gap-2">
                    <span className="font-medium text-white">{req.name}</span>
                    <span className={`badge border text-xs ${requestStatusBadgeClass(req.status)}`}>
                      {requestStatusLabel(req.status)}
                    </span>
                    {req.review_notes ? (
                      <span className="text-gray-500 text-xs w-full">{req.review_notes}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          {resolvedRequests.length > 0 ? (
            <>
              <p className="text-xs text-gray-500 mb-2">Recent decisions</p>
              <ul className="space-y-2 list-none">
                {resolvedRequests.map((req) => (
                  <li key={req.id} className="text-sm text-gray-300 flex flex-wrap items-center gap-2">
                    <span className="font-medium text-white">{req.name}</span>
                    <span className={`badge border text-xs ${requestStatusBadgeClass(req.status)}`}>
                      {requestStatusLabel(req.status)}
                    </span>
                    {req.status === 'approved' && req.resulting_shop_id ? (
                      <Link to={`/shops/${req.resulting_shop_id}`} className="text-xs text-orange-300 hover:underline">
                        View shop
                      </Link>
                    ) : null}
                    {req.review_notes ? (
                      <span className="text-gray-500 text-xs w-full">{req.review_notes}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </section>
      )}

      <form className="flex gap-2 mb-6" onSubmit={onSearch} role="search">
        <input
          type="search"
          className="input flex-1"
          placeholder="Search by name, city, or specialty"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search shops"
          autoComplete="off"
        />
        <Button type="submit" variant="secondary">
          Search
        </Button>
      </form>

      <div aria-live="polite" className="sr-only">
        {loading ? 'Loading shops…' : `${shops.length} ${shops.length === 1 ? 'shop' : 'shops'} shown`}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" role="status" aria-busy="true">
          <span className="sr-only">Loading shops…</span>
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="h-36 rounded-xl border border-white/[0.06] bg-[#0c0c0c]/70 animate-pulse motion-reduce:animate-none"
              aria-hidden
            />
          ))}
        </div>
      ) : error ? (
        <div>
          <div role="alert" className="alert-error mb-4">
            {error}
          </div>
          <Button type="button" variant="secondary" onClick={() => void load(activeQuery, page)}>
            Try again
          </Button>
        </div>
      ) : shops.length === 0 ? (
        <EmptyState
          icon={<BuildingStorefrontIcon className="w-14 h-14" />}
          title={activeQuery ? `No shops match “${activeQuery}”` : 'No shops listed yet'}
          description={
            activeQuery
              ? 'Try a different name or specialty, or request a shop to be added.'
              : 'Approved shops that work on documented builds appear here.'
          }
          action={
            isAuthenticated ? (
              <Button type="button" variant="primary" onClick={() => setRequestOpen(true)}>
                Request a shop
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 list-none">
            {shops.map((shop) => (
              <li key={shop.id}>
                <ShopCard shop={shop} />
              </li>
            ))}
          </ul>
          {showPagination && (
            <nav className="flex items-center justify-center gap-3 mt-8" aria-label="Shop directory pages">
              <Button type="button" variant="secondary" size="sm" disabled={!hasPrev} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                Previous
              </Button>
              <span className="text-sm text-gray-400" aria-current="page">
                Page {page}
              </span>
              <Button type="button" variant="secondary" size="sm" disabled={!hasNext} onClick={() => setPage((p) => p + 1)}>
                Next
              </Button>
            </nav>
          )}
        </>
      )}

      <ShopRequestModal
        isOpen={requestOpen}
        onClose={() => setRequestOpen(false)}
        initialName={activeQuery}
        onSubmitted={() => {
          if (isAuthenticated) {
            void apiService.getMyShopRequests().then(setMyRequests);
          }
        }}
      />
    </PageShell>
  );
};

export default ShopDirectoryPage;
